import { format, parse, parseISO, isValid } from 'date-fns'

export type CalendarItem = {
  id: string
  title?: string
  datetime?: string
  datetime_start?: string
  datetime_end?: string
  date_created?: string
  location?: string | null
  conversation_id?: string | null
  client_id?: string[] | null
  tags?: string[] | string | null
  all_day?: boolean
}

export type CalendarItemWithDate = CalendarItem & {
  parsedStart: Date | null
  parsedEnd: Date | null
}

export type CalendarEventDisplay = {
  id: string
  title: string
  start: Date
  end: Date
  allDay: boolean
  tags?: string[] | string | null
}

export function parseEventDate(value?: string, allDay = false) {
  if (!value) return null

  if (allDay) {
    const datePart = value.replace(' ', 'T').split('T')[0]
    const parsed = parse(datePart, 'yyyy-MM-dd', new Date())
    return isValid(parsed) ? parsed : null
  }

  const normalized = value.replace(' ', 'T')
  const parsed = parseISO(normalized)
  return isValid(parsed) ? parsed : null
}

export function parseCalendarItemDates(items: CalendarItem[]) {
  return items
    .map((item) => ({
      ...item,
      parsedStart: parseEventDate(item.datetime_start || item.datetime, item.all_day),
      parsedEnd: parseEventDate(item.datetime_end, item.all_day),
    }))
    .sort((a, b) => {
      const aTime = a.parsedStart?.getTime() ?? 0
      const bTime = b.parsedStart?.getTime() ?? 0
      return aTime - bTime
    })
}

export function toDisplayEvents(items: CalendarItemWithDate[]) {
  return items.map((item) => {
    const start = item.parsedStart ?? new Date()
    const end =
      item.parsedEnd ??
      (item.parsedStart ? new Date(item.parsedStart.getTime() + 60 * 60 * 1000) : new Date())

    return {
      id: item.id,
      title: item.title || 'Untitled event',
      start,
      end,
      allDay: item.all_day ?? false,
      tags: item.tags,
    }
  })
}

export function buildEventMap(events: CalendarEventDisplay[]) {
  const map = new Map<string, { title: string }[]>()

  events.forEach((event) => {
    const key = format(event.start, 'yyyy-MM-dd')
    const list = map.get(key) ?? []
    list.push({ title: event.title })
    map.set(key, list)
  })

  return map
}
